import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { Scissors } from "lucide-react";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/admin-sidebar";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/trpc/trpc";
import { useEffect } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminLayout,
});

/** Which salon the owner panel will open on — shown so admins always know. */
function ActiveSalonHint() {
  const { data: salon } = trpc.salon.getOwnerSalon.useQuery();
  if (!salon) return null;
  return (
    <>
      <Separator orientation="vertical" className="mx-2 h-4" />
      <Link
        to="/"
        className="flex items-center gap-1.5 text-sm hover:underline"
        title="Przejdź do panelu tego salonu"
      >
        <Scissors className="size-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">Aktywny salon:</span>
        <span className="font-medium">{salon.name}</span>
      </Link>
    </>
  );
}

function AdminLayout() {
  const navigate = useNavigate();
  const { data: adminMe, isLoading, error } = trpc.admin.getMe.useQuery();

  useEffect(() => {
    if (error) {
      toast.error("Brak dostępu do panelu admina");
      navigate({ to: "/" });
    }
  }, [error, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Ładowanie...</div>
      </div>
    );
  }

  if (!adminMe) {
    return null;
  }

  return (
    <SidebarProvider>
      <AdminSidebar isSuperAdmin={adminMe.isSuperAdmin} />
      <SidebarInset>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <span className="text-sm text-muted-foreground">
            {adminMe.adminOrgName ?? "Super Admin"}
          </span>
          <ActiveSalonHint />
        </header>
        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
